/**
 * Home's prompt box: the product's own ChatComposer, bound to the
 * organization's new-chat draft — the same draft Chat opens — so what you
 * type here is what Chat shows, with the same model picker, access,
 * attachments and shortcuts, in the same frame and over the same context
 * strip (space and jurisdictions) as Chat's new-chat composer. Sending
 * hands the draft to the chat view (draftSendHandoff) and opens it; the chat
 * view sends through its one send path.
 *
 * @module components/home/HomeComposer
 */
import {
  scopedThreadKey,
  scopeProjectRef,
  scopeThreadRef,
} from "@amoreg/client-runtime/environment";
import type {
  EnvironmentId,
  ModelSelection,
  ProjectId,
  ProviderInstanceId,
  ServerProvider,
} from "@amoreg/contracts";
import { useAtomValue } from "@effect/atom-react";
import { useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from "react";

import {
  useComposerDraftStore,
  type ComposerImageAttachment,
  type DraftId,
} from "../../composerDraftStore";
import { requestDraftSend } from "../../draftSendHandoff";
import { useNewThreadHandler } from "../../hooks/useHandleNewThread";
import { useEnvironmentSettings } from "../../hooks/useSettings";
import { useTheme } from "../../hooks/useTheme";
import { optionsForModelSwitch, readModelEffortPreference } from "../../modelEffortPreferences";
import { resolveAppModelSelectionForInstance } from "../../modelSelection";
import { getProviderModelCapabilities } from "../../providerModels";
import { usePrimaryEnvironment } from "../../state/environments";
import { primaryServerKeybindingsAtom } from "../../state/server";
import { buildDraftThreadRouteParams } from "../../threadRoutes";
import { setToolAccessChoice, useToolAccessChoice } from "../../toolAccess";
import { BranchToolbar } from "../BranchToolbar";
import { ChatComposer, type ChatComposerHandle, type ChatComposerProps } from "../chat/ChatComposer";
import { type ComposerAccessMode } from "../chat/ComposerAccessControl";
import { ChatComposerFrame } from "../chat/ChatComposerFrame";
import { draftForScope, homeDraftScope, type HomeScopedDraft } from "./homeLogic";
import { ExpandedImageDialog } from "../chat/ExpandedImageDialog";
import { toastManager } from "../ui/toast";
import type { ExpandedImagePreview } from "../chat/ExpandedImagePreview";

const noop = () => {};
const noModelDisabledReason = () => null;
/** Why an attachment was not added (too large, unreadable, over the cap): Home says so in a toast. */
const showComposerError = (_threadId: unknown, error: string | null) => {
  if (error) toastManager.add({ type: "error", title: "Attachment not added", description: error });
};

/** What Home can do to its composer from outside: fill it, e.g. "Ask Amoreg to prepare". */
export interface HomeComposerApi {
  /** Fills the composer and focuses it; the person reads, edits and sends. Home never sends for them. */
  readonly prefill: (prompt: string) => void;
}

export interface HomeComposerProject {
  readonly id: ProjectId;
  readonly environmentId: EnvironmentId;
  readonly defaultModelSelection: ModelSelection | null;
}

/**
 * The org's new-chat draft, prepared without opening it. Reuses the draft
 * Chat would open (the new-thread handler's own rule), so Home and Chat never
 * hold two different unsent messages for one organization.
 */
function useOrgDraft(project: HomeComposerProject): DraftId | null {
  const handleNewThread = useNewThreadHandler();
  // The draft belongs to the organization it was opened for: moving to another
  // org's Home must not keep showing, editing or sending the first one's.
  const scope = homeDraftScope(project.environmentId, project.id);
  const [draft, setDraft] = useState<HomeScopedDraft<DraftId> | null>(null);
  const draftId = draftForScope(draft, scope);
  // A sent draft is promoted to a real thread; Home then needs a fresh one.
  const promoted = useComposerDraftStore((store) =>
    draftId ? (store.getDraftSession(draftId)?.promotedTo ?? null) !== null : false,
  );
  const missing = useComposerDraftStore((store) =>
    draftId ? store.getDraftSession(draftId) === null : false,
  );
  useEffect(() => {
    if (draftId !== null && !promoted && !missing) return;
    let cancelled = false;
    void handleNewThread(scopeProjectRef(project.environmentId, project.id), {
      navigate: false,
    }).then((next) => {
      if (!cancelled) setDraft(next === null ? null : { id: next, scope });
    });
    return () => {
      cancelled = true;
    };
  }, [draftId, handleNewThread, missing, project.environmentId, project.id, promoted, scope]);
  return draftId;
}

export function HomeComposer({
  project,
  placeholder,
  apiRef,
  onHasTextChange,
  framePrompt,
}: {
  readonly project: HomeComposerProject;
  readonly placeholder: string;
  readonly apiRef?: RefObject<HomeComposerApi | null> | undefined;
  /** Told whenever the box gains or loses its last character. */
  readonly onHasTextChange?: (hasText: boolean) => void;
  /** The message sent for what was typed, for a page that asks about something of its own (a Probe interview). */
  readonly framePrompt?: ((prompt: string) => string) | undefined;
}) {
  const draftId = useOrgDraft(project);
  if (draftId === null) {
    return <div aria-hidden className="mx-auto h-[176px] max-w-3xl rounded-[16px] bg-foreground/[0.03]" />;
  }
  return (
    <HomeDraftComposer
      key={draftId}
      project={project}
      draftId={draftId}
      placeholder={placeholder}
      apiRef={apiRef}
      onHasTextChange={onHasTextChange}
      framePrompt={framePrompt}
    />
  );
}

function HomeDraftComposer({
  project,
  draftId,
  placeholder,
  apiRef,
  onHasTextChange,
  framePrompt,
}: {
  readonly project: HomeComposerProject;
  readonly draftId: DraftId;
  readonly placeholder: string;
  readonly apiRef: RefObject<HomeComposerApi | null> | undefined;
  readonly onHasTextChange: ((hasText: boolean) => void) | undefined;
  readonly framePrompt: ((prompt: string) => string) | undefined;
}) {
  const navigate = useNavigate();
  const environmentId = project.environmentId;
  const settings = useEnvironmentSettings(environmentId);
  const keybindings = useAtomValue(primaryServerKeybindingsAtom);
  const { resolvedTheme } = useTheme();
  const primaryEnvironment = usePrimaryEnvironment();
  // The composer takes a mutable list; the environment's config is readonly.
  const serverProviders = primaryEnvironment?.serverConfig?.providers;
  const providerStatuses = useMemo<ServerProvider[]>(() => [...(serverProviders ?? [])], [serverProviders]);
  const draftThreadId = useComposerDraftStore(
    (store) => store.getDraftSession(draftId)?.threadId ?? null,
  );

  const composerRef = useRef<ChatComposerHandle | null>(null);
  const hasText = useComposerDraftStore(
    (store) => (store.getComposerDraft(draftId)?.prompt ?? "").trim().length > 0,
  );
  useEffect(() => onHasTextChange?.(hasText), [hasText, onHasTextChange]);
  const promptRef = useRef("");
  const composerImagesRef = useRef<ComposerImageAttachment[]>([]);
  const composerTerminalContextsRef = useRef<ChatComposerProps["composerTerminalContextsRef"]["current"]>([]);
  const composerElementContextsRef = useRef<ChatComposerProps["composerElementContextsRef"]["current"]>([]);
  const focusComposer = useCallback(() => composerRef.current?.focusAtEnd(), []);
  // Chat's own per-chat access choice, keyed by this draft's thread: the chat
  // view that sends it reads the same choice.
  const threadKey =
    draftThreadId === null ? null : scopedThreadKey(scopeThreadRef(environmentId, draftThreadId));
  const toolAccessMode = useToolAccessChoice(threadKey);
  const handleToolAccessModeChange = useCallback(
    (mode: ComposerAccessMode) => {
      if (threadKey) setToolAccessChoice(threadKey, mode);
    },
    [threadKey],
  );
  const onSend: ChatComposerProps["onSend"] = (event) => {
    event?.preventDefault();
    const context = composerRef.current?.getSendContext();
    if (!context) return;
    const hasContent =
      context.prompt.trim().length > 0 || context.images.length > 0 || context.files.length > 0;
    if (!hasContent) return;
    // Written into the draft before the chat view mounts it, as useStartChatWithText writes.
    if (framePrompt) useComposerDraftStore.getState().setPrompt(draftId, framePrompt(context.prompt));
    requestDraftSend(draftId);
    void navigate({
      to: "/$org/draft/$draftId",
      params: buildDraftThreadRouteParams(draftId, project.id),
    });
  };
  useEffect(() => {
    if (!apiRef) return;
    apiRef.current = {
      prefill: (prompt) => {
        // Through the editor's own insert, so its text and the draft agree;
        // a half-written message keeps its words and gains the request.
        const handle = composerRef.current;
        if (!handle) return;
        const written = handle.readSnapshot().value.trim().length > 0;
        // The insert focuses the editor once it lands; a focusAtEnd here would re-read the editor first and clear it.
        handle.insertTextAtEnd(written ? `\n\n${prompt}` : prompt);
        document.querySelector("[data-home-composer]")?.scrollIntoView({ block: "nearest", behavior: "smooth" });
      },
    };
    return () => {
      apiRef.current = null;
    };
  }, [apiRef, draftId]);
  // An attached image's Preview opens the same dialog as in the chat view.
  const [expandedImage, setExpandedImage] = useState<ExpandedImagePreview | null>(null);
  const closeExpandedImage = useCallback(() => setExpandedImage(null), []);

  const onProviderModelSelect = useCallback(
    (instanceId: ProviderInstanceId, model: string) => {
      const entry = providerStatuses.find((snapshot) => snapshot.instanceId === instanceId);
      const resolvedModel = resolveAppModelSelectionForInstance(
        instanceId,
        settings,
        providerStatuses,
        model,
      );
      if (!resolvedModel) return;
      const store = useComposerDraftStore.getState();
      const currentOptions = store.getComposerDraft(draftId)?.modelSelectionByProvider[instanceId]?.options;
      const nextOptions = entry
        ? optionsForModelSwitch({
            caps: getProviderModelCapabilities(entry.models, resolvedModel, entry.driver),
            currentOptions,
            preferredEffort: readModelEffortPreference(instanceId, resolvedModel),
          })
        : currentOptions;
      const selection: ModelSelection = {
        instanceId,
        model: resolvedModel,
        ...(nextOptions && nextOptions.length > 0 ? { options: nextOptions } : {}),
      };
      store.setModelSelection(draftId, selection, { replaceOptions: true });
      store.setStickyModelSelection(selection);
      focusComposer();
    },
    [draftId, focusComposer, providerStatuses, settings],
  );

  if (draftThreadId === null) return null;
  const routeThreadRef = scopeThreadRef(environmentId, draftThreadId);

  return (
    <>
    <ChatComposerFrame
      data-home-composer=""
      contextStrip={<BranchToolbar environmentId={environmentId} threadId={draftThreadId} draftId={draftId} />}
    >
      <ChatComposer
        composerRef={composerRef}
        composerDraftTarget={draftId}
        environmentId={environmentId}
        routeKind="draft"
        routeThreadRef={routeThreadRef}
        draftId={draftId}
        // The draft's thread, as the chat view passes it on a draft route:
        // attachments are staged against it, and without it they are dropped.
        activeThreadId={draftThreadId}
        activeThreadEnvironmentId={undefined}
        activeThread={undefined}
        providerCatalogKnown={primaryEnvironment?.serverConfig != null}
        promptHistoryMessages={[]}
        isServerThread={false}
        isLocalDraftThread
        forceExpandedOnMobile
        projectSelectionRequired={false}
        // A new chat has no session yet: the chat view passes the same.
        phase="disconnected"
        isConnecting={false}
        isSendBusy={false}
        sendDisabledReason={null}
        isPreparingWorktree={false}
        externalDrawerAttached={false}
        environmentUnavailable={null}
        activePendingApproval={null}
        pendingApprovals={[]}
        pendingUserInputs={[]}
        activePendingProgress={null}
        activePendingResolvedAnswers={null}
        activePendingIsResponding={false}
        activePendingDraftAnswers={{}}
        activePendingQuestionIndex={0}
        respondingRequestIds={[]}
        lockedProvider={null}
        providerStatuses={providerStatuses}
        activeProjectDefaultModelSelection={project.defaultModelSelection}
        activeThreadModelSelection={undefined}
        activeThreadActivities={undefined}
        resolvedTheme={resolvedTheme}
        settings={settings}
        keybindings={keybindings}
        terminalOpen={false}
        gitCwd={null}
        getTimelineScrollableNode={() => null}
        isTimelineAtLogicalEnd={() => true}
        timelineOverflows={false}
        onComposerOverlayHeightChange={noop}
        onRestingChange={noop}
        promptRef={promptRef}
        composerImagesRef={composerImagesRef}
        composerTerminalContextsRef={composerTerminalContextsRef}
        composerElementContextsRef={composerElementContextsRef}
        followUpBehavior={settings.followUpBehavior}
        onSend={onSend}
        onInterrupt={noop}
        onRespondToApproval={async () => {}}
        onSelectActivePendingUserInputOption={noop}
        onAdvanceActivePendingUserInput={noop}
        onPreviousActivePendingUserInputQuestion={noop}
        onChangeActivePendingUserInputCustomAnswer={noop}
        onProviderModelSelect={onProviderModelSelect}
        getModelDisabledReason={noModelDisabledReason}
        toolAccessMode={toolAccessMode}
        onToolAccessModeChange={handleToolAccessModeChange}
        focusComposer={focusComposer}
        scheduleComposerFocus={focusComposer}
        // Home has no thread view to show a rejected attachment's reason in.
        setThreadError={showComposerError}
        onExpandImage={setExpandedImage}
        placeholder={placeholder}
      />
    </ChatComposerFrame>
    {expandedImage && (
      <ExpandedImageDialog
        key={`${expandedImage.images[expandedImage.index]?.src ?? "image"}:${expandedImage.index}`}
        preview={expandedImage}
        onClose={closeExpandedImage}
      />
    )}
    </>
  );
}
